import type { AppEntry } from "../registry/apps";
import type { Bounds } from "./camera";

export type Span = { col: number; row: number };

export type PlacedTile =
  | {
      kind: "score";
      key: string;
      col: number;
      row: number;
      colSpan: number;
      rowSpan: number;
      x: number;
      y: number;
      w: number;
      h: number;
      tilt: number;
    }
  | {
      kind: "app";
      key: string;
      col: number;
      row: number;
      colSpan: number;
      rowSpan: number;
      x: number;
      y: number;
      w: number;
      h: number;
      tilt: number;
      app: AppEntry;
    };

export type SpaceOrb = {
  key: string;
  x: number;
  y: number;
  size: number;
  tone: "acid" | "teal" | "signal" | "kraft";
};

export type WallLayout = {
  tiles: PlacedTile[];
  orbs: SpaceOrb[];
  width: number;
  height: number;
  home: Bounds;
};

const HOME_SPANS: Span[] = [
  { col: 3, row: 3 },
  { col: 2, row: 2 },
  { col: 4, row: 2 },
  { col: 2, row: 3 },
  { col: 3, row: 2 },
  { col: 2, row: 2 },
  { col: 5, row: 3 },
  { col: 3, row: 3 },
  { col: 2, row: 2 },
  { col: 4, row: 3 },
  { col: 3, row: 2 },
  { col: 2, row: 4 },
];

export const HOME_COLS = 12;
export const HOME_CELL = 118;
export const HOME_GAP = 7;

type RawTile =
  | {
      kind: "score";
      key: string;
      colSpan: number;
      rowSpan: number;
      tilt: number;
    }
  | {
      kind: "app";
      key: string;
      colSpan: number;
      rowSpan: number;
      tilt: number;
      app: AppEntry;
    };

function hash01(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export function hashTilt(n: number): number {
  return hash01(n) * 3.2 - 1.6;
}

function sizeOf(span: Span, cell: number, gap: number): { w: number; h: number } {
  return {
    w: span.col * cell + Math.max(0, span.col - 1) * gap,
    h: span.row * cell + Math.max(0, span.row - 1) * gap,
  };
}

function place(
  raw: RawTile,
  col: number,
  row: number,
  cell: number,
  gap: number,
  originX: number,
  originY: number,
): PlacedTile {
  const { w, h } = sizeOf({ col: raw.colSpan, row: raw.rowSpan }, cell, gap);
  const x = originX + col * (cell + gap);
  const y = originY + row * (cell + gap);
  if (raw.kind === "score") {
    return {
      kind: "score",
      key: raw.key,
      colSpan: raw.colSpan,
      rowSpan: raw.rowSpan,
      tilt: raw.tilt,
      col,
      row,
      x,
      y,
      w,
      h,
    };
  }
  return {
    kind: "app",
    key: raw.key,
    colSpan: raw.colSpan,
    rowSpan: raw.rowSpan,
    tilt: raw.tilt,
    app: raw.app,
    col,
    row,
    x,
    y,
    w,
    h,
  };
}

function packDense(
  items: RawTile[],
  cols: number,
  cell: number,
  gap: number,
  originX: number,
  originY: number,
): { tiles: PlacedTile[]; width: number; height: number } {
  const occ: boolean[][] = [];

  const rowHas = (r: number): boolean[] => {
    const existing = occ[r];
    if (existing) return existing;
    const next = Array.from({ length: cols }, () => false);
    occ[r] = next;
    return next;
  };

  const fits = (c: number, r: number, cs: number, rs: number): boolean => {
    if (c + cs > cols) return false;
    for (let y = r; y < r + rs; y += 1) {
      const row = rowHas(y);
      for (let x = c; x < c + cs; x += 1) {
        if (row[x]) return false;
      }
    }
    return true;
  };

  const fill = (c: number, r: number, cs: number, rs: number) => {
    for (let y = r; y < r + rs; y += 1) {
      const row = rowHas(y);
      for (let x = c; x < c + cs; x += 1) {
        row[x] = true;
      }
    }
  };

  const tiles: PlacedTile[] = [];
  let maxRow = 0;

  for (const item of items) {
    const cs = Math.min(item.colSpan, cols);
    const rs = item.rowSpan;
    let found: { c: number; r: number } | null = null;
    const maxSearch = Math.max(occ.length, 1) + 48;
    for (let r = 0; r < maxSearch && !found; r += 1) {
      for (let c = 0; c <= cols - cs; c += 1) {
        if (fits(c, r, cs, rs)) {
          found = { c, r };
          break;
        }
      }
    }
    const slot = found ?? { c: 0, r: occ.length };
    fill(slot.c, slot.r, cs, rs);
    tiles.push(place({ ...item, colSpan: cs }, slot.c, slot.r, cell, gap, originX, originY));
    maxRow = Math.max(maxRow, slot.r + rs);
  }

  return {
    tiles,
    width: cols * cell + Math.max(0, cols - 1) * gap,
    height: Math.max(1, maxRow * cell + Math.max(0, maxRow - 1) * gap),
  };
}

function union(tiles: readonly PlacedTile[]): Bounds {
  let x = Infinity;
  let y = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  for (const t of tiles) {
    x = Math.min(x, t.x);
    y = Math.min(y, t.y);
    x2 = Math.max(x2, t.x + t.w);
    y2 = Math.max(y2, t.y + t.h);
  }
  if (!Number.isFinite(x)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x, y, w: x2 - x, h: y2 - y };
}

function makeOrbs(width: number, height: number): SpaceOrb[] {
  const tones: SpaceOrb["tone"][] = ["acid", "teal", "signal", "kraft"];
  const count = Math.max(8, Math.min(16, Math.round((width * height) / 1_000_000)));
  const orbs: SpaceOrb[] = [];
  for (let i = 0; i < count; i += 1) {
    const a = hash01(i + 3.1);
    const b = hash01(i + 9.7);
    const tone = tones[i % tones.length] ?? "acid";
    orbs.push({
      key: `orb-${i}`,
      x: a * width,
      y: b * height,
      size: 90 + hash01(i + 21) * 220,
      tone,
    });
  }
  return orbs;
}

/**
 * Pack the score tile plus the apps it is given. Callers pass live ships only.
 * There is no ghost field — empty quest slots stay in the HUD, not on the wall.
 * `home` is the whole collage so the camera frames real tiles, not a blank grid.
 */
export function buildWall(apps: readonly AppEntry[]): WallLayout {
  const items: RawTile[] = [
    {
      kind: "score",
      key: "score",
      colSpan: 4,
      rowSpan: 3,
      tilt: -0.8,
    },
    ...apps.map((app, i) => {
      const span = HOME_SPANS[i % HOME_SPANS.length] ?? { col: 3, row: 2 };
      return {
        kind: "app" as const,
        key: `app-${app.surface}-${app.slug}`,
        colSpan: span.col,
        rowSpan: span.row,
        tilt: hashTilt(i + 9),
        app,
      };
    }),
  ];

  const packed = packDense(items, HOME_COLS, HOME_CELL, HOME_GAP, 0, 0);
  const width = Math.max(packed.width, 1);
  const height = Math.max(packed.height, 1);

  return {
    tiles: packed.tiles,
    orbs: makeOrbs(width, height),
    width,
    height,
    home: union(packed.tiles),
  };
}

export function tilesInView(
  tiles: readonly PlacedTile[],
  view: Bounds,
): PlacedTile[] {
  const out: PlacedTile[] = [];
  const x2 = view.x + view.w;
  const y2 = view.y + view.h;
  for (const t of tiles) {
    if (t.x < x2 && t.x + t.w > view.x && t.y < y2 && t.y + t.h > view.y) {
      out.push(t);
    }
  }
  return out;
}

export function orbsInView(orbs: readonly SpaceOrb[], view: Bounds): SpaceOrb[] {
  return orbs.filter((o) => {
    const pad = o.size;
    return (
      o.x + pad > view.x &&
      o.x - pad < view.x + view.w &&
      o.y + pad > view.y &&
      o.y - pad < view.y + view.h
    );
  });
}
